import { AppContextService } from '../../../services/app-context.service';
import { Optional, ElementRef, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { viewContextVisible } from '../../../shared/view-context';
import { LocalStorageService } from '../../../services/local-storage.service';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { BrIconComponent } from '../../../shared/ui/icon/icon.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ConfirmationDialogService } from
  '../../../shared/ui/confirmation-dialog/confirmation-dialog.service';
import { EMPTY, Observable, of } from 'rxjs';
import { finalize, switchMap } from 'rxjs/operators';
import {
  dynamicApiErrorMessage,
  DynamicFrameworkApiService,
} from './dynamic-framework-api.service';
import {
  DynamicColumn,
  DynamicWorkflowDefinition,
  DynamicWorkflowNode,
  DynamicWorkflowStatus,
  DynamicWorkflowTransition,
  DynamicWorkflowWrite,
} from './dynamic-framework.models';
import {
  LeadsV2FieldDialogComponent,
  LeadsV2FieldDialogData,
} from './leads-v2-field-dialog.component';
import {
  LeadsV2PublishWorkflowDialogComponent,
  PublishWorkflowDialogResult,
} from './leads-v2-publish-workflow-dialog.component';
import { DynamicWorkflowGraphComponent } from './dynamic-workflow-graph.component';
import {
  autoLayout,
  NodePosition,
  placeNewNode,
  readLayout,
  renameLayout,
  WorkflowLayout,
  writeLayout,
} from './dynamic-workflow-layout';
import {
  validateWorkflowDraft,
  WorkflowIssue,
  WorkflowValidationSummary,
} from './dynamic-workflow-validation';
import {
  copyDynamicColumn,
  listWorkflowFields,
  updateSharedField,
} from './dynamic-workflow-fields';

interface Snapshot {
  nodes: DynamicWorkflowNode[];
  transitions: DynamicWorkflowTransition[];
  layout: WorkflowLayout;
}

const HISTORY_LIMIT = 60;
const DRAFT_LAYOUT_KEY = 'draft';

/** Five node colours, so the swatch row stays readable and every one of them
 *  holds a white dot against the node's white card. */
export const NODE_COLORS = ['#6a1b9a', '#0d6efd', '#0f8a5f', '#c77700', '#c62828'];

@Component({
  selector: 'app-leads-v2-workflow-config',
  standalone: true,
  imports: [CommonModule, FormsModule, BrIconComponent, DynamicWorkflowGraphComponent],
  templateUrl: './leads-v2-workflow-config.component.html',
  styleUrls: ['./leads-v2-workflow-config.component.css'],
})
export class LeadsV2WorkflowConfigComponent implements OnInit, OnDestroy {
  @Input() workflowKey: string | null = null;
  @Input() createMode = false;

  @Output() workflowCreated = new EventEmitter<string>();
  @Output() backRequested = new EventEmitter<void>();

  @ViewChild(DynamicWorkflowGraphComponent) graph?: DynamicWorkflowGraphComponent;

  readonly colors = NODE_COLORS;

  draft: DynamicWorkflowWrite = emptyWorkflow();
  layout: WorkflowLayout = {};
  status: DynamicWorkflowStatus | 'new' = 'new';
  updatedAt = '';
  loading = false;
  loadError = '';
  saving = false;

  /**
   * One selection, two surfaces.
   *
   * The open row in the tree and the selected node on the canvas are the same
   * thing, so clicking either one moves the other. They used to be separate
   * pieces of state that both had to be kept right by hand.
   */
  selectedNodeIndex: number | null = null;
  selectedTransitionIndex: number | null = null;

  nodesOpen = true;
  transitionsOpen = true;

  validation: WorkflowValidationSummary = validateWorkflowDraft(emptyWorkflow());
  /** Publish-specific issues, including the selected start node. Cleared by
   *  the next edit because they describe a graph that no longer exists. */
  serverIssues: WorkflowIssue[] = [];

  /** Company and user, used only to scope the stored node arrangement. */
  private past: Snapshot[] = [];
  private future: Snapshot[] = [];
  /**
   * The last settled state, and what an undo step actually pushes.
   *
   * Not every edit can snapshot before it happens. `[(ngModel)]` has already
   * written the new value by the time an input fires `change`, so a snapshot
   * taken there captures the edit rather than what preceded it, and undo would
   * restore what the user just typed. Keeping the previous settled state
   * separately makes both orders - snapshot-then-mutate and mutate-then-report
   * - push the same correct thing.
   */
  private baseline: Snapshot = { nodes: [], transitions: [], layout: {} };
  /** The graph as last persisted, so leaving the page can warn about work the
   *  server has not seen. Node positions are excluded: they live in this
   *  browser and are written the moment a node stops moving. */
  private saved = '';

  constructor(
    private readonly api: DynamicFrameworkApiService,
    private readonly storage: LocalStorageService,
    private readonly confirmation: ConfirmationDialogService,
    private readonly dialog: MatDialog,
    private readonly snackBar: MatSnackBar,
    @Optional() private readonly context?: AppContextService,
    @Optional() private readonly element?: ElementRef<HTMLElement>,
  ) {}

  private contextRegistration?: Subscription;
  private restoredView?: { version?: number; nodeKey: string | null; transition: { from: string; to: string } | null;
    nodesOpen: boolean; transitionsOpen: boolean };

  ngOnDestroy(): void { this.restoredView = undefined; this.contextRegistration?.unsubscribe(); }

  private restoreViewSelection(): void {
    const state = this.restoredView;
    if (!state || this.loading) return;
    this.restoredView = undefined;
    if (this.dirty || state.version !== this.context?.restorationVersion) return;
    const node = this.draft.nodes.findIndex(item => item.key === state.nodeKey);
    const transition = this.draft.transitions.findIndex(item =>
      item.fromNodeKey === state.transition?.from && item.toNodeKey === state.transition?.to);
    this.selectedNodeIndex = node < 0 ? null : node;
    this.selectedTransitionIndex = transition < 0 ? null : transition;
    this.nodesOpen = state.nodesOpen;
    this.transitionsOpen = state.transitionsOpen;
  }

  ngOnInit(): void {
    this.contextRegistration = this.context?.registerView({
      key: `workflow-editor:${this.workflowKey ?? 'new'}`, name: 'Workflow editor',
      active: () => viewContextVisible(this.element),
      read: () => ({ nodeKey: this.selectedNodeKey,
        transition: this.selectedTransition ? { from: this.selectedTransition.fromNodeKey, to: this.selectedTransition.toNodeKey } : null,
        nodesOpen: this.nodesOpen, transitionsOpen: this.transitionsOpen }),
      describe: state => JSON.stringify({ workflowKey: this.workflowKey, ...state, status: this.status,
        loading: this.loading, dirty: this.dirty }),
      restore: state => { this.restoredView = { ...state, version: this.context?.restorationVersion }; this.restoreViewSelection(); },
    });
    this.reload();
  }

  // --- derived -------------------------------------------------------------

  get readOnly(): boolean {
    return this.status === 'published';
  }

  get dirty(): boolean {
    return !this.readOnly && this.serialise() !== this.saved;
  }

  get selectedNode(): DynamicWorkflowNode | null {
    return this.selectedNodeIndex == null
      ? null
      : this.draft.nodes[this.selectedNodeIndex] ?? null;
  }

  get selectedTransition(): DynamicWorkflowTransition | null {
    return this.selectedTransitionIndex == null
      ? null
      : this.draft.transitions[this.selectedTransitionIndex] ?? null;
  }

  get selectedNodeKey(): string | null {
    return this.selectedNode?.key ?? null;
  }

  get canUndo(): boolean {
    return !this.readOnly && !this.graph?.interactionActive && this.past.length > 0;
  }

  get canRedo(): boolean {
    return !this.readOnly && !this.graph?.interactionActive && this.future.length > 0;
  }

  nodeLabel(key: string): string {
    return this.draft.nodes.find(node => node.key === key)?.label ?? key;
  }

  /** Issues about one node, shown on its own row rather than in a separate
   *  list the user has to map back onto the tree themselves. */
  issuesFor(nodeKey: string): WorkflowIssue[] {
    return this.allIssues.filter(issue => issue.nodeKey === nodeKey);
  }

  /** Issues about the workflow as a whole, which belong to no row. */
  get workflowIssues(): WorkflowIssue[] {
    return this.allIssues.filter(issue => !issue.nodeKey);
  }

  get errorNodeKeys(): Set<string> {
    return new Set([
      ...this.validation.errorNodeKeys,
      ...this.serverIssues
        .map(issue => issue.nodeKey)
        .filter((key): key is string => !!key),
    ]);
  }

  private get allIssues(): WorkflowIssue[] {
    return this.validation.issues.concat(this.serverIssues);
  }

  /**
   * Adds a transition between the first pair of nodes that does not already
   * have one, so the new row is a legal edge the user can then retarget rather
   * than a duplicate the checks immediately complain about.
   */
  addTransition(): void {
    if (this.readOnly || this.draft.nodes.length < 2) return;
    const pair = this.firstFreePair();
    if (!pair) {
      this.snackBar.open('Every pair of nodes is already connected.', 'OK', { duration: 4000 });
      return;
    }
    this.pushHistory();
    this.draft.transitions = [...this.draft.transitions, {
      fromNodeKey: pair.from,
      toNodeKey: pair.to,
    }];
    this.transitionsOpen = true;
    this.selectTransition(this.draft.transitions.length - 1);
    this.touch();
  }

  private firstFreePair(): { from: string; to: string } | null {
    const taken = new Set(this.draft.transitions.map(edge => `${edge.fromNodeKey} ${edge.toNodeKey}`));
    const ordered = [...this.draft.nodes].sort((a, b) => a.displayOrder - b.displayOrder);
    for (const from of ordered) {
      for (const to of ordered) {
        if (from.key === to.key || taken.has(`${from.key} ${to.key}`)) continue;
        return { from: from.key, to: to.key };
      }
    }
    return null;
  }

  // --- loading -------------------------------------------------------------

  reload(): void {
    this.loading = true;
    this.loadError = '';
    const workflow$: Observable<DynamicWorkflowDefinition | null> = this.createMode
      ? of(null)
      : this.workflowKey
        ? this.api.getWorkflow(this.workflowKey)
        : of(null);
    workflow$
      .pipe(finalize(() => { this.loading = false; this.restoreViewSelection(); }))
      .subscribe({
        next: workflow => {
          if (workflow) {
            this.useDefinition(workflow);
          } else if (this.createMode) {
            this.draft = emptyWorkflow();
            this.status = 'new';
            this.updatedAt = '';
            this.layout = readLayout(this.storage, this.layoutKey);
            this.saved = this.serialise();
            // The editor is hidden while loading. Centre the initial node after
            // Angular has created and measured the canvas viewport.
            setTimeout(() => this.graph?.zoomToFit());
          } else {
            this.loadError = 'Workflow not found.';
          }
          this.touch(false);
        },
        error: error => {
          this.loadError = dynamicApiErrorMessage(error, 'Could not load the workflow.');
        },
      });
  }

  // --- selection -----------------------------------------------------------

  /** A tree row opens what is already selected and closes it again. */
  toggleNode(index: number): void {
    this.selectedNodeIndex = this.selectedNodeIndex === index ? null : index;
    this.selectedTransitionIndex = null;
  }

  selectNodeByKey(key: string): void {
    const index = this.draft.nodes.findIndex(node => node.key === key);
    if (index >= 0) this.nodesOpen = true;
    this.selectedNodeIndex = index < 0 ? null : index;
    this.selectedTransitionIndex = null;
  }

  selectTransition(index: number): void {
    const exists = index >= 0 && index < this.draft.transitions.length;
    if (exists) this.transitionsOpen = true;
    this.selectedTransitionIndex = exists ? index : null;
    this.selectedNodeIndex = null;
  }

  toggleTransition(index: number): void {
    this.selectedTransitionIndex = this.selectedTransitionIndex === index ? null : index;
    this.selectedNodeIndex = null;
  }

  clearSelection(): void {
    this.selectedNodeIndex = null;
    this.selectedTransitionIndex = null;
  }

  // --- graph edits ---------------------------------------------------------

  addNode(): void {
    if (this.readOnly) return;
    this.pushHistory();
    const index = this.draft.nodes.length;
    const node = createWorkflowNode(this.nextNodeKey(), `Step ${index + 1}`, index);
    this.draft.nodes = [...this.draft.nodes, node];
    const position = this.graph?.newNodePositionAtViewportCenter() ?? placeNewNode(index);
    this.layout = { ...this.layout, [node.key]: position };
    this.selectNodeByKey(node.key);
    this.touch();
  }

  addField(node: DynamicWorkflowNode): void {
    if (this.readOnly) return;
    const nodeKeys = new Set(node.fields.map(field => field.key));
    const workflowFields = listWorkflowFields(this.draft.nodes);
    this.openFieldDialog({
      title: 'Add field',
      availableFields: workflowFields.filter(field => !nodeKeys.has(field.key)),
      existingKeys: workflowFields.map(field => field.key),
      keyLocked: false,
      typeLocked: false,
    }).subscribe(field => {
      if (!field) return;
      this.pushHistory();
      field.sortOrder = node.fields.length;
      field.isActive = true;
      node.fields = [...node.fields, field];
      this.touch();
    });
  }

  editField(node: DynamicWorkflowNode, index: number): void {
    if (this.readOnly) return;
    const current = node.fields[index];
    if (!current) return;
    this.openFieldDialog({
      title: `Edit ${current.label}`,
      field: current,
      existingKeys: listWorkflowFields(this.draft.nodes)
        .filter(field => field.key !== current.key)
        .map(field => field.key),
      keyLocked: false,
      typeLocked: false,
    }).subscribe(field => {
      if (!field) return;
      this.pushHistory();
      this.draft.nodes = updateSharedField(
        this.draft.nodes,
        current.key,
        node.key,
        index,
        field,
      );
      this.touch();
    });
  }

  removeField(node: DynamicWorkflowNode, index: number): void {
    if (this.readOnly || !node.fields[index]) return;
    this.pushHistory();
    node.fields = node.fields
      .filter((_, fieldIndex) => fieldIndex !== index)
      .map((field, fieldIndex) => ({ ...field, sortOrder: fieldIndex }));
    this.touch();
  }

  fieldTypeLabel(field: DynamicColumn): string {
    return {
      shortText: 'Short text',
      number: 'Number',
      date: 'Date',
      boolean: 'Yes / No',
      choice: 'Choice',
    }[field.dataType];
  }

  removeSelected(): void {
    if (this.readOnly || this.graph?.interactionActive) return;
    if (this.selectedNodeIndex != null) this.removeNode(this.selectedNodeIndex);
    else if (this.selectedTransitionIndex != null) this.removeTransition(this.selectedTransitionIndex);
  }

  removeNode(index: number): void {
    const node = this.draft.nodes[index];
    if (!node || this.readOnly) return;
    if (node.fields.length) {
      this.confirmation.confirm({
        title: 'Remove node?',
        message: `“${node.label}” contains ${node.fields.length} field${node.fields.length === 1 ? '' : 's'}. Removing it will also remove its field assignments and connected transitions.`,
        confirmLabel: 'Remove node',
        danger: true,
      }).subscribe(confirmed => {
        if (!confirmed) return;
        const currentIndex = this.draft.nodes.findIndex(item => item.key === node.key);
        if (currentIndex >= 0) this.deleteNode(currentIndex);
      });
      return;
    }
    this.deleteNode(index);
  }

  private deleteNode(index: number): void {
    const node = this.draft.nodes[index];
    if (!node || this.readOnly) return;
    this.pushHistory();
    this.draft.nodes = this.draft.nodes.filter((_, nodeIndex) => nodeIndex !== index);
    this.draft.nodes.forEach((item, nodeIndex) => item.displayOrder = nodeIndex);
    this.draft.transitions = this.draft.transitions.filter(
      edge => edge.fromNodeKey !== node.key && edge.toNodeKey !== node.key);
    const next = { ...this.layout };
    delete next[node.key];
    this.layout = next;
    this.clearSelection();
    this.touch();
  }

  removeTransition(index: number): void {
    if (this.readOnly) return;
    this.pushHistory();
    this.draft.transitions = this.draft.transitions.filter((_, edgeIndex) => edgeIndex !== index);
    this.clearSelection();
    this.touch();
  }

  onTransitionCreated(edge: { fromNodeKey: string; toNodeKey: string }): void {
    if (this.readOnly) return;
    this.pushHistory();
    this.draft.transitions = [...this.draft.transitions, {
      fromNodeKey: edge.fromNodeKey,
      toNodeKey: edge.toNodeKey,
    }];
    this.selectTransition(this.draft.transitions.length - 1);
    this.touch();
  }

  onTransitionReconnected(change: {
    index: number;
    fromNodeKey: string;
    toNodeKey: string;
  }): void {
    if (this.readOnly) return;
    const current = this.draft.transitions[change.index];
    if (!current
      || (current.fromNodeKey === change.fromNodeKey
        && current.toNodeKey === change.toNodeKey)) return;

    this.pushHistory();
    this.draft.transitions = this.draft.transitions.map((edge, index) =>
      index === change.index
        ? {
            ...edge,
            fromNodeKey: change.fromNodeKey,
            toNodeKey: change.toNodeKey,
          }
        : edge);
    this.selectTransition(change.index);
    this.touch();
  }

  onNodeMoved(move: { key: string; position: NodePosition }): void {
    this.pushHistory();
    this.layout = { ...this.layout, [move.key]: move.position };
    this.touch();
  }

  applyAutoLayout(): void {
    if (this.readOnly) return;
    this.pushHistory();
    this.layout = autoLayout(this.draft.nodes, this.draft.transitions);
    this.touch();
    // Arranging without framing leaves a wide graph running off the right of a
    // canvas that just changed under the user. Fit, then it is centred.
    setTimeout(() => this.graph?.zoomToFit());
  }

  setColor(node: DynamicWorkflowNode, color: string): void {
    if (this.readOnly) return;
    this.pushHistory();
    node.color = color;
    this.touch();
  }

  /** One inline edit - a name, a field, a toggle - is one undo step. */
  editStep(): void {
    this.pushHistory();
    this.touch();
  }

  // --- history -------------------------------------------------------------

  undo(): void {
    if (!this.canUndo || this.graph?.interactionActive) return;
    this.future.unshift(this.baseline);
    this.restore(this.past.pop()!);
  }

  redo(): void {
    if (!this.canRedo || this.graph?.interactionActive) return;
    this.past.push(this.baseline);
    this.restore(this.future.shift()!);
  }

  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    const target = event.target as HTMLElement | null;
    const tag = target?.tagName;
    if (tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA' || target?.isContentEditable) return;

    const historyShortcut = (event.metaKey || event.ctrlKey)
      && event.key.toLowerCase() === 'z';
    const deleteShortcut = event.key === 'Delete' || event.key === 'Backspace';
    if (this.graph?.interactionActive && (historyShortcut || deleteShortcut)) {
      event.preventDefault();
      return;
    }

    if (historyShortcut) {
      event.preventDefault();
      if (event.shiftKey) this.redo();
      else this.undo();
      return;
    }
    if (deleteShortcut) {
      if (this.selectedNodeIndex == null && this.selectedTransitionIndex == null) return;
      event.preventDefault();
      this.removeSelected();
    }
  }

  private pushHistory(): void {
    this.past.push(this.baseline);
    if (this.past.length > HISTORY_LIMIT) this.past.shift();
    this.future = [];
  }

  private snapshot(): Snapshot {
    return {
      nodes: this.draft.nodes.map(node => this.copyNode(node)),
      transitions: this.draft.transitions.map(edge => ({ ...edge })),
      layout: { ...this.layout },
    };
  }

  private restore(snapshot: Snapshot): void {
    this.draft = { ...this.draft, nodes: snapshot.nodes, transitions: snapshot.transitions };
    this.layout = snapshot.layout;
    this.clearSelection();
    this.touch();
  }

  /**
   * Republishes the draft to the canvas and rechecks it.
   *
   * The arrays are replaced rather than mutated in place: the canvas reads them
   * as inputs, and an input whose reference never changes never reaches
   * ngOnChanges, so an edit made through the node modal would not redraw.
   */
  private touch(persist = true): void {
    this.draft = {
      ...this.draft,
      nodes: [...this.draft.nodes],
      transitions: [...this.draft.transitions],
    };
    this.validation = validateWorkflowDraft(this.draft);
    this.serverIssues = [];
    this.baseline = this.snapshot();
    if (persist) writeLayout(this.storage, this.layoutKey, this.layout);
  }

  /** Tenant/user identity is supplied by LocalStorageService. */
  private get layoutKey(): string {
    return this.status === 'new' || !this.workflowKey ? DRAFT_LAYOUT_KEY : this.workflowKey;
  }

  private get draftLayoutKey(): string { return DRAFT_LAYOUT_KEY; }

  // --- persistence ---------------------------------------------------------

  save(): void {
    const error = this.blockingError();
    if (error) {
      this.snackBar.open(error, 'OK', { duration: 5000 });
      return;
    }
    this.saving = true;
    const isNew = this.status === 'new';
    const workflowKey = isNew
      ? this.workflowKey ?? createWorkflowKey(this.draft.label)
      : this.workflowKey;
    if (!workflowKey) {
      this.saving = false;
      return;
    }
    const request = isNew
      ? this.api.createWorkflow({ ...this.draft, key: workflowKey })
      : this.api.updateWorkflow(workflowKey, {
          ...this.draft,
          expectedUpdatedAt: this.updatedAt,
        });
    request.pipe(finalize(() => this.saving = false)).subscribe({
      next: definition => {
        if (isNew) {
          const from = this.draftLayoutKey;
          this.workflowKey = definition.key;
          this.status = definition.status;
          renameLayout(this.storage, from, this.layoutKey);
          this.workflowCreated.emit(definition.key);
        }
        this.useDefinition(definition);
        this.touch(false);
        this.snackBar.open(
          isNew ? 'Workflow created.' : 'Workflow saved.', undefined, { duration: 2500 });
      },
      error: requestError => this.showError(requestError, 'Could not save the workflow.'),
    });
  }

  /**
   * Publishing takes what the server holds, not what is on screen. An unsaved
   * edit would therefore publish the previous graph and then be overwritten by
   * the definition that comes back - the edit would be gone with no error
   * anywhere. So publishing requires a saved draft, and the server validates
   * it once more before the irreversible step.
   */
  publish(): void {
    if (!this.workflowKey || !this.updatedAt) return;
    if (this.dirty) {
      this.snackBar.open('Save your changes before publishing.', 'OK', { duration: 5000 });
      return;
    }
    if (this.validation.errorCount) {
      this.snackBar.open('Fix the errors before publishing.', 'OK', { duration: 5000 });
      return;
    }
    this.api.validateWorkflow(this.draft).subscribe({
      next: result => {
        if (!result.isValid) {
          this.serverIssues = result.issues.map(issue => ({
            severity: 'error' as const,
            message: issue.message,
            nodeKey: issue.nodeKey ?? undefined,
          }));
          this.snackBar.open('The server rejected this graph. Review the highlighted nodes.', 'OK', {
            duration: 6000,
          });
          return;
        }
        this.dialog.open(LeadsV2PublishWorkflowDialogComponent, {
          data: {
            label: this.draft.label,
            nodes: this.draft.nodes,
            transitions: this.draft.transitions,
            layout: this.layout,
          },
          width: '820px',
          maxWidth: 'calc(100vw - 32px)',
          autoFocus: 'select',
        }).afterClosed().subscribe((dialogResult?: PublishWorkflowDialogResult) => {
          if (!dialogResult || dialogResult.action !== 'publish') return;
          const publishValidation = validateWorkflowDraft(
            this.draft,
            dialogResult.startNodeKey,
          );
          if (publishValidation.errorCount) {
            this.serverIssues = publishValidation.issues;
            this.snackBar.open(
              'Every node must be reachable from the selected start node.',
              'OK',
              { duration: 6000 },
            );
            return;
          }
          this.api.publishWorkflow(
            this.workflowKey!,
            this.updatedAt,
            dialogResult.startNodeKey,
          ).subscribe({
            next: definition => {
              this.useDefinition(definition);
              this.touch(false);
              this.snackBar.open('Workflow published.', undefined, { duration: 2500 });
            },
            error: error => this.showPublishError(error),
          });
        });
      },
      error: error => this.showError(error, 'Could not validate the workflow.'),
    });
  }

  unpublish(): void {
    if (!this.workflowKey || !this.updatedAt || !this.readOnly || this.saving) return;
    this.confirmation.confirm({
      title: 'Unpublish workflow?',
      message: 'This returns the workflow to Draft so it can be edited. '
        + 'A workflow with data cannot be unpublished.',
      confirmLabel: 'Unpublish',
    }).pipe(
      switchMap(confirmed => {
        if (!confirmed) return EMPTY;
        this.saving = true;
        return this.api.unpublishWorkflow(this.workflowKey!, this.updatedAt);
      }),
      finalize(() => this.saving = false),
    ).subscribe({
      next: definition => {
        this.useDefinition(definition);
        this.touch(false);
        this.snackBar.open('Workflow returned to Draft.', undefined, { duration: 2500 });
      },
      error: error => this.showError(error, 'Could not unpublish the workflow.'),
    });
  }

  back(): void {
    this.backRequested.emit();
  }

  private useDefinition(definition: DynamicWorkflowDefinition): void {
    this.status = definition.status;
    this.updatedAt = definition.updatedAt;
    this.workflowKey = definition.key;
    this.draft = {
      label: definition.label,
      expectedUpdatedAt: definition.updatedAt,
      nodes: definition.nodes.map(node => this.copyNode(node)),
      transitions: definition.transitions.map(edge => ({ ...edge })),
    };
    const stored = readLayout(this.storage, this.layoutKey);
    this.layout = Object.keys(stored).length
      ? stored
      : autoLayout(this.draft.nodes, this.draft.transitions);
    this.saved = this.serialise();
    this.past = [];
    this.future = [];
    this.clearSelection();
  }

  /** The part of the draft the server owns, for the unsaved-changes check. */
  private serialise(): string {
    return JSON.stringify({
      label: this.draft.label,
      nodes: this.draft.nodes,
      transitions: this.draft.transitions,
    });
  }

  private nextNodeKey(): string {
    let index = this.draft.nodes.length + 1;
    while (this.draft.nodes.some(node => node.key === `step_${index}`)) index++;
    return `step_${index}`;
  }

  private copyNode(node: DynamicWorkflowNode): DynamicWorkflowNode {
    return {
      ...node,
      fields: node.fields.map(field => copyDynamicColumn(field)),
    };
  }

  private openFieldDialog(data: LeadsV2FieldDialogData) {
    return this.dialog.open<
      LeadsV2FieldDialogComponent,
      LeadsV2FieldDialogData,
      DynamicColumn
    >(LeadsV2FieldDialogComponent, {
      data,
      width: '600px',
      maxWidth: 'calc(100vw - 32px)',
      maxHeight: 'calc(100vh - 32px)',
      panelClass: 'br-dialog-panel',
      disableClose: true,
    }).afterClosed();
  }

  /** What stops a request being sent at all. The live validation reports on the
   *  shape of the graph; this is about what the API will refuse outright. */
  private blockingError(): string | null {
    if (!this.draft.label.trim()) return 'The workflow needs a name.';
    if (!this.draft.nodes.length) return 'Add at least one node.';
    const keys = this.draft.nodes.map(node => node.key.trim());
    if (keys.some(key => !/^[a-z][a-z0-9_]{0,63}$/.test(key))) return 'A node key has an invalid format.';
    if (new Set(keys).size !== keys.length) return 'Node keys must be unique.';
    if (this.draft.nodes.some(node => !node.label.trim())) return 'Every node needs a name.';
    return null;
  }

  private showError(error: unknown, fallback: string): void {
    this.snackBar.open(dynamicApiErrorMessage(error, fallback), 'OK', { duration: 6000 });
  }

  private showPublishError(error: unknown): void {
    if (error instanceof HttpErrorResponse && error.status === 422) {
      const body = error.error as {
        issues?: Array<{ message?: unknown; nodeKey?: unknown }>;
      } | null;
      const issues = body?.issues
        ?.filter(issue => typeof issue.message === 'string')
        .map(issue => ({
          severity: 'error' as const,
          message: issue.message as string,
          nodeKey: typeof issue.nodeKey === 'string' ? issue.nodeKey : undefined,
        })) ?? [];
      if (issues.length) {
        this.serverIssues = issues;
        this.snackBar.open('The workflow cannot be published. See the highlighted nodes.', 'OK', {
          duration: 6000,
        });
        return;
      }
    }

    this.showError(error, 'Could not publish the workflow.');
  }
}

function emptyWorkflow(): DynamicWorkflowWrite {
  return {
    label: '',
    nodes: [createWorkflowNode('start', 'Start', 0)],
    transitions: [],
  };
}

function createWorkflowKey(label: string): string {
  const stem = label.trim().toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/^[^a-z]+/, '')
    .slice(0, 40) || 'workflow';
  const suffix = `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
  return `${stem}_${suffix}`.slice(0, 64);
}

function createWorkflowNode(
  key: string,
  label: string,
  displayOrder: number,
): DynamicWorkflowNode {
  return {
    fields: [],
    key,
    label,
    color: NODE_COLORS[displayOrder % NODE_COLORS.length],
    displayOrder,
  };
}
